<template>
	<div style="width: 100%; height: 100%; position: relative">
		<div id="map"></div>
		<div
			v-if="showMapLoadingOverlay"
			class="map-loading-overlay"
			:class="{ 'map-loading-overlay--hidden': mapLoadingOverlayHidden }"
			role="status"
			aria-live="polite"
		>
			<div class="map-loading-overlay__panel">
				<div class="map-loading-overlay__spinner"></div>
				<p
					v-if="mapLoadingMessage"
					class="map-loading-overlay__message"
				>
					{{ mapLoadingMessage }}
				</p>
				<p	
					v-if="shouldDisplayAccuracy"
					class="map-loading-overlay__accuracy"
				>
					{{ accuracyLabel }}
				</p>
			</div>
		</div>
		<!-- Overlay to block all user interactions until unlock -->
		<div v-if="interactionsLocked" class="map-interaction-blocker" aria-hidden="true" aria-busy="true"
			@touchstart.prevent.stop @touchmove.prevent.stop @touchend.prevent.stop @wheel.prevent.stop
			@mousedown.prevent.stop @mousemove.prevent.stop @mouseup.prevent.stop @click.prevent.stop></div>
		<!-- <div style="position: fixed; color: white; top: 50px">
			{{ locationAccuracy }}
		</div> -->
		<!-- <small v-if="poorAccuracy" class="accuracy-indicator"
			>Poor location accuracy</small -->
		>
		<LoadingComponent v-if="loading" />
		<!-- <div
			style="
				position: fixed;
				z-index: 1000000;
				color: #fff;
				top: 50%;
				left: 50%;
			"
		>
			<div>{{ activePool ? activePool.name : "" }}</div>
			<div>
				{{
					activeDropLocation
						? activeDropLocation.placeName
						: "not in dropzone"
				}}
			</div>
		</div> -->
	</div>
</template>

<script lang="ts">
import { addDropZoneCircles, mountZonesOnMap, selectNearestZones } from "@/utils/geolocationTools";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { DropLocation, DropPool } from "@/types/dropPool";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { VItem } from "@/types/vItem";
import { defineComponent } from "vue";
import { Action, ActionType } from "@/types/customPage";
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore";
import { vItemStore } from "@/store/pinia_modules/vItemStore";
import { navigationStore } from "@/store/pinia_modules/navigationStore";
import { poolSegmentStore } from "@/store/pinia_modules/poolSegmentStore";
import { authStore } from "@/store/pinia_modules/authStore";
import { categoryStore } from "@/store/pinia_modules/categoryStore";
import { v4 as uuidv4 } from "uuid";
import { getUserLocation } from "@/utils/geolocationTools";
// import { template } from "@/templates/marker.ts";

const myAccessToken = import.meta.env.VITE_APP_MAPBOX_ACCESS_TOKEN;
const accuracyThreshold = 200;
const dropZoomThreshold = 16.0;
const defaultMapStyleUrl = "mapbox://styles/innereality/cmewg3arc01fp01qw68a58ttc";
const dropFlowMaxDurationMs = 20000;
const dropFlowFallbackRevealMs = 4000;
const dropFlowRetryIntervalMs = 600;
const dropFlowStartDelayMs = 2000;
const dropFlowAccuracyRecheckMs = 500;

type MapFogOptions = Record<string, unknown>;

interface MapVisualConfig {
	enableFog: boolean;
	fogOptions?: MapFogOptions;
	enable3dBuildings: boolean;
}

const mapVisualConfig: MapVisualConfig = {
	enableFog: false,
	enable3dBuildings: false,
};

const default3dLayerIds = ["3d-buildings", "3d-buildings-shadow", "3d-buildings-top"];

type DropPipelineState =
	| "idle"
	| "pending"
	| "waitingZoom"
	| "waitingIdle"
	| "ready"
	| "dropping"
	| "completed"
	| "failed";

type DropTriggerSource =
	| "initial-flow"
	| "zone-verified"
	| "zoom-crossed"
	| "fallback"
	| "manual"
	| "retry";

interface DropReadinessSnapshot {
	zoomSatisfied: boolean;
	idleSatisfied: boolean;
	forced: boolean;
	zoom: number;
}

type MapLoadingState =
	| "initializing"
	| "locating"
	| "animating"
	| "ready"
	| "fallback"
	| "failed";

export default defineComponent({
	props: {
		element: {
			type: Object,
			required: true,
		},
	},
	components: { LoadingComponent },
	data() {
		return {
			// loading: false,

			reachedAccuracy: false,
			itemsDropped: false,
			userMarker: null as mapboxgl.Marker | null,
			locations: [] as DropLocation[],
			dropType: "pin",
			map: null as mapboxgl.Map | null,
			markers: [] as mapboxgl.Marker[],
			locationMarkers: {} as { [key: string]: mapboxgl.Marker },
			locationMarkersOnScreen: {} as { [key: string]: mapboxgl.Marker },
			vatMarkers: [] as mapboxgl.Marker[],
			showLocationsAndVats: false,
			selectedMarkerIndex: "",
			arButton: null as HTMLDivElement | null,
			startActiveDropPoolCheck: false,
			initZoom: true,
			vatsAdded: false,
			loadedLocations: false,
			mapStyle: defaultMapStyleUrl,
			userManualMapControl: false,
			// mapZooming removed
			canRenderMarkers: false,
			dropAttempted: false,
			introAnimationRan: false,
			fitBoundsOnLoad: false,
			fallbackRevealAllowed: false,
			interactionsLocked: true,
			mapLoadingState: "initializing" as MapLoadingState,
			showMapLoadingOverlay: true,
			mapLoadingOverlayHidden: false,
			mapFallbackPromptTimer: null as number | null,
			mapAutoRevealTimer: null as number | null,
			mapOverlayHideTimer: null as number | null,
			dropFlowCheckCancelled: true,
			fallbackPopupShown: false,
			dropFlowZoneVerified: false,
			dropPipelineState: "idle" as DropPipelineState,
			dropPipelineTrigger: null as DropTriggerSource | null,
			dropLifecycleActive: false,
			dropReadinessSnapshot: null as DropReadinessSnapshot | null,
			dropInFlight: false,
		};
	},

	// beforeMount(): void{
	// this.checkPermissions()
	// },

	mounted(): void {
		this.init();
	},
	beforeUnmount() {
		this.clearMapLoadingTimers();
		this.clearOverlayHideTimer();
		this.dropFlowCheckCancelled = true;
		navigationStore().stopWatchingPosition();
		navigationStore().clearDeniedPermissions();
		// Defensive: clear any legacy dropPool watcher if still running
		const legacyId = dropPoolStore().getWatcher;
		if (legacyId != -1) {
			navigator.geolocation.clearWatch(legacyId);
			dropPoolStore().saveWatcher(-1);
		}
		if (this.map) {
			navigationStore().setPoorAccuracy(false);
			const map = this.map!;
			const safe = (fn: () => void, label: string) => {
				try { fn(); } catch (e) { console.debug(`[Map teardown] ${label} failed`, e); }
			};

			// Detach listeners via loop
			const listeners: Array<[string, any]> = [
				["dragstart", this.dragStartListener],
				["load", this.loadListener],
				["zoom", this.zoomListener],
				["zoomend", this.zoomEndListener],
				["sourcedata", this.sourceDataListener],
			];
			for (const [evt, handler] of listeners) {
				safe(() => map.off(evt as any, handler), `off ${evt}`);
			}

			// Remove layers/sources if present
			const layers = ["clusters", "cluster-count", "radius-circles"];
			for (const id of layers) {
				if (map.getLayer(id)) safe(() => map.removeLayer(id), `removeLayer ${id}`);
			}
			const sources = ["radius-circles", "locations-source"];
			for (const id of sources) {
				if (map.getSource(id)) safe(() => map.removeSource(id), `removeSource ${id}`);
			}

			safe(() => map.remove(), "map.remove()");
			this.map = null;
			const mapElement = document.getElementById("map");
			if (mapElement) {
				mapElement.innerHTML = "";
			}
			this.startActiveDropPoolCheck = false;
		}
	},
	watch: {
		userLocation(to: [number, number], from: [number, number]) {
			if (to && from) {
				dropPoolStore().saveActiveDropPool();
				// Trigger zone check on location change (cached in store)
				if (this.activePool) {
					const lat = to[1];
					const lng = to[0];
					dropPoolStore().zoneCheck({ poolID: this.activePool.id!, latitude: lat, longitude: lng });
				}
				this.moveUserIcon();
			}
			this.maybeStartIntroFromLocation();
		},
		action(to: Action): void {
			if (to) {
				if (to.type == "claim-item") {
					this.claimItem();
				} else if (to.type == "center-map") {
					this.updateUserLocation();
				}
			}
		},

		activePool(to: DropPool, from: DropPool) {
			if (to && from && this.startActiveDropPoolCheck) {
				this.addRemoveDroppedItems(from.id! || "");
			}
			// Render zones when active pool changes
			if (to) {
				if (!from || to.id !== from.id) {
					this.dropFlowZoneVerified = false;
					this.itemsDropped = false;
					this.dropAttempted = false;
					this.dropInFlight = false;
				}
				this.renderZones();
			}
		},

		activeDropLocation(to: DropLocation) {
			if (this.startActiveDropPoolCheck) {
				if (this.activePool) {
					// this.addRemoveDroppedItems(this.activePool.id!);
					this.removeRadiusCircleLayer();
					if (this.map!.getZoom() >= 14) {
						this.loadRadiusCircleLayer(to);
					}
				} else {
					this.removeItemsFromMap();
				}
			}
		},

	},
	methods: {
		async init() {
			this.resetMapLoadingOverlay();
			this.hideARBtn();
			this.dropAttempted = false;
			this.itemsDropped = false;
			this.dropInFlight = false;

			// Request location permission using the existing getUserLocation function
			getUserLocation();

			// Check if we already have a valid user location
			if (this.userLocation[0] !== 0 && this.userLocation[1] !== 0) {
				console.log("Already have user location:", this.userLocation);
			}

			navigationStore().clearDeniedPermissions();
			this.loadedLocations = false;
			this.startActiveDropPoolCheck = false;
			this.initZoom = true;
			this.vatsAdded = false;

			//clear all the items
			if (vItemStore().items.length > 0) {
				// if there are no pending items, clear the items
				if (vItemStore().getPendingItems.length <= 0) {
					vItemStore().clearItems();
				}
			}

			this.resetDropPoolLocationState();
			// this.startWatchingPosition();

			//Get pools categories and prizes
			if (vItemStore().getPendingItems.length <= 0) {
				await this.getPoolsCategoriesPrizes();
			}
			//Get campaign segments
			// await this.getCampaignSegments();
			//get active dropPool if one exists
			// dropPoolStore().saveActiveDropPool();
			// //Get locations
			this.getLocations();
			// //Check droptype
			this.getDroptype();
			dropPoolStore().setLoading(false);
			this.initMap();
			this.addUserMarker();
			this.dropFlowCheckCancelled = false;
			this.fallbackPopupShown = false;
			this.dropFlowZoneVerified = false;
			const dropFlowStartAt = Date.now();

			const scheduleNextDropCheck = (delayMs: number) => {
				if (this.dropFlowCheckCancelled || this.dropAttempted) {
					return;
				}
				window.setTimeout(runDropFlowCheck, delayMs);
			};

			const maybeTriggerFallbackReveal = (elapsedMs: number) => {
				if (this.dropAttempted) {
					return;
				}
				if (this.fallbackRevealAllowed) {
					return;
				}
				if (elapsedMs >= dropFlowFallbackRevealMs && vItemStore().getPendingItems.length <= 0) {
					this.fallbackRevealAllowed = true;
					this.revealMapSmoothly("fallback");
					if (!this.fallbackPopupShown) {
						navigationStore().emitAction({ type: ActionType.OPEN_POPUP, value: "out-of-drop-zone" });
						this.fallbackPopupShown = true;
					}
					this.unlockInteractions();
					if (!this.dropAttempted && this.isDropEligible()) {
						this.runDropLifecycle("fallback", { forceAfterMs: 0, timeoutMs: dropFlowFallbackRevealMs + 3000 }).catch((error) => {
							this.logDropEvent("fallback-trigger-error", { error });
						});
					}
				}
			};

			const runDropFlowCheck = async () => {
				if (this.dropFlowCheckCancelled || this.dropAttempted) {
					return;
				}
				if (!this.map) {
					scheduleNextDropCheck(300);
					return;
				}
				const elapsed = Date.now() - dropFlowStartAt;
				if (!this.hasResolvedUserLocation) {
					maybeTriggerFallbackReveal(elapsed);
					scheduleNextDropCheck(dropFlowRetryIntervalMs);
					return;
				}
				if (this.locationAccuracy > 500 && this.locationAccuracy < 10000) {
					await new Promise((resolve) => setTimeout(resolve, dropFlowAccuracyRecheckMs));
				}
				navigationStore().setPoorAccuracy(this.locationAccuracy >= accuracyThreshold);
				dropPoolStore().saveActiveDropPool();
				const activePool = this.activePool;
				this.renderZones();

			let handled = false;
			if (activePool && !this.dropAttempted) {
				if (activePool.dropType === "gps" && activePool.id) {
					let shouldAttemptDrop = this.dropFlowZoneVerified;
					if (!this.dropFlowZoneVerified) {
						try {
							const lat = this.userLocation[1];
							const lng = this.userLocation[0];
							const resp = await dropPoolStore().zoneCheck({ poolID: activePool.id, latitude: lat, longitude: lng, force: true });
							if (resp && resp.data && resp.data.allowed) {
								this.dropFlowZoneVerified = true;
								shouldAttemptDrop = true;
							} else {
								const pinPoolFound = dropPoolStore().saveActivePinPool();
								if (pinPoolFound) {
									this.dropFlowZoneVerified = true;
									shouldAttemptDrop = true;
								}
							}
						} catch (error) {
							console.warn("[Map] zoneCheck failed", error);
						}
					}
					if (shouldAttemptDrop) {
						handled = await this.runDropLifecycle("zone-verified", { forceAfterMs: dropFlowFallbackRevealMs + 1500, timeoutMs: dropFlowMaxDurationMs });
					}
				} else if (activePool.dropType === "pin") {
					this.dropFlowZoneVerified = true;
					handled = await this.runDropLifecycle("zone-verified", { forceAfterMs: dropFlowFallbackRevealMs + 1500, timeoutMs: dropFlowMaxDurationMs });
				}
			} else if (!this.dropAttempted) {
				handled = await this.runDropLifecycle("initial-flow", { forceAfterMs: dropFlowFallbackRevealMs + 1500, timeoutMs: dropFlowMaxDurationMs });
			}

				if (handled || this.dropAttempted) {
					return;
				}

				maybeTriggerFallbackReveal(elapsed);
				if (!this.dropAttempted) {
					if (elapsed < dropFlowMaxDurationMs) {
						scheduleNextDropCheck(dropFlowRetryIntervalMs);
					} else {
						this.dropFlowCheckCancelled = true;
						if (!this.fallbackRevealAllowed) {
							this.fallbackRevealAllowed = true;
							this.revealMapSmoothly("fallback");
						}
						if (!this.fallbackPopupShown && vItemStore().getPendingItems.length <= 0) {
							navigationStore().emitAction({ type: ActionType.OPEN_POPUP, value: "out-of-drop-zone" });
							this.fallbackPopupShown = true;
						}
						this.mapLoadingState = "failed";
						this.unlockInteractions();
						if (!this.dropAttempted && this.isDropEligible()) {
							this.runDropLifecycle("fallback", { forceAfterMs: 0, timeoutMs: dropFlowFallbackRevealMs + 3000 }).catch((error) => {
								this.logDropEvent("fallback-timeout-error", { error });
							});
						}
					}
				}
			};

			scheduleNextDropCheck(dropFlowStartDelayMs);
		},
		resetMapLoadingOverlay() {
			this.clearMapLoadingTimers();
			this.clearOverlayHideTimer();
			this.dropFlowCheckCancelled = true;
			this.fallbackPopupShown = false;
			this.dropFlowZoneVerified = false;
			this.dropPipelineState = "idle";
			this.dropPipelineTrigger = null;
			this.dropLifecycleActive = false;
			this.dropReadinessSnapshot = null;
			this.dropAttempted = false;
			this.itemsDropped = false;
			this.dropInFlight = false;
			this.mapLoadingState = "initializing";
			this.showMapLoadingOverlay = true;
			this.mapLoadingOverlayHidden = false;
			this.fallbackRevealAllowed = false;
		},
		maybeStartIntroFromLocation() {
			if (!this.map) {
				return;
			}
			if (this.introAnimationRan) {
				return;
			}
			if (!this.hasResolvedUserLocation) {
				return;
			}
			this.startIntroCameraAnimation().catch(() => {});
		},
		logDropEvent(stage: string, extras: Record<string, unknown> = {}) {
			const zoom = this.map ? Number(this.map.getZoom()).toFixed(2) : "n/a";
			console.debug(`[DropPipeline] ${stage}`, {
				state: this.dropPipelineState,
				trigger: this.dropPipelineTrigger,
				zoom,
				dropAttempted: this.dropAttempted,
				...extras,
			});
		},
		isDropEligible(): boolean {
			if (this.dropAttempted) {
				return false;
			}
			if (vItemStore().getPendingItems.length > 0) {
				return true;
			}
			if (this.dropFlowZoneVerified) {
				return true;
			}
			if (this.activePool && this.activePool.dropType === "pin") {
				return true;
			}
			return false;
		},
		async runDropLifecycle(trigger: DropTriggerSource, options?: { forceAfterMs?: number; timeoutMs?: number }): Promise<boolean> {
			if (this.dropAttempted) {
				return true;
			}
			if (!this.map) {
				this.logDropEvent("skip-no-map", { trigger });
				return false;
			}
			if (!this.isDropEligible()) {
				this.logDropEvent("skip-not-eligible", { trigger });
				return false;
			}
			if (this.dropLifecycleActive) {
				this.logDropEvent("skip-already-running", { trigger });
				return false;
			}
			const forceAfterMs = options?.forceAfterMs ?? 4500;
			const timeoutMs = options?.timeoutMs ?? 9000;
			this.dropLifecycleActive = true;
			this.dropPipelineTrigger = trigger;
			this.dropPipelineState = "pending";
			this.logDropEvent("start", { trigger, forceAfterMs, timeoutMs });
			try {
				if (!this.introAnimationRan) {
					try {
						await this.startIntroCameraAnimation();
					} catch (error) {
						this.logDropEvent("intro-error", { trigger, error });
					}
				}
				if (this.dropAttempted) {
					this.dropPipelineState = "completed";
					return true;
				}
				const snapshot = await this.ensureDropReadiness({ forceAfterMs, timeoutMs });
				this.dropReadinessSnapshot = snapshot;
				if (this.dropAttempted) {
					return true;
				}
				await this.performDrop(trigger, snapshot);
				return this.dropAttempted;
			} catch (error) {
				console.warn("[DropPipeline] drop lifecycle failed", { trigger, error });
				this.dropPipelineState = "failed";
				return false;
			} finally {
				this.dropLifecycleActive = false;
				this.dropPipelineTrigger = null;
			}
		},
		async ensureDropReadiness({ forceAfterMs, timeoutMs }: { forceAfterMs: number; timeoutMs: number }): Promise<DropReadinessSnapshot> {
			if (!this.map) {
				return { zoomSatisfied: false, idleSatisfied: false, forced: true, zoom: 0 };
			}
			this.dropPipelineState = "waitingZoom";
			const zoomReached = await this.ensureZoomAtLeast(dropZoomThreshold, { forceAfterMs, timeoutMs });
			if (!this.map) {
				return { zoomSatisfied: false, idleSatisfied: false, forced: true, zoom: 0 };
			}
			const zoom = this.map.getZoom();
			const zoomSatisfied = zoom >= dropZoomThreshold - 0.01;
			this.dropPipelineState = "waitingIdle";
			const idleSatisfied = await this.waitForMapIdle(Math.min(4000, timeoutMs));
			this.dropPipelineState = "ready";
			const forced = !zoomReached || !idleSatisfied;
			this.logDropEvent("ready", {
				zoom: Number(zoom).toFixed(2),
				zoomSatisfied,
				idleSatisfied,
				forced,
			});
			return {
				zoomSatisfied,
				idleSatisfied,
				forced,
				zoom,
			};
		},
		async waitForMapIdle(timeoutMs = 3000): Promise<boolean> {
			if (!this.map) {
				return false;
			}
			if (!this.map.isMoving()) {
				return true;
			}
			return new Promise<boolean>((resolve) => {
				let resolved = false;
				let timeoutId = 0;
				const idleHandler = () => finish(true);
				const moveEndHandler = () => finish(true);
				const cleanup = () => {
					window.clearTimeout(timeoutId);
					if (!this.map) {
						return;
					}
					try {
						this.map.off('idle', idleHandler as any);
						this.map.off('moveend', moveEndHandler as any);
					} catch (_) {
						/* noop */
					}
				};
				const finish = (value: boolean) => {
					if (!resolved) {
						resolved = true;
						cleanup();
						resolve(value);
					}
				};
				timeoutId = window.setTimeout(() => finish(false), timeoutMs);
				this.map!.once('idle', idleHandler as any);
				this.map!.once('moveend', moveEndHandler as any);
			});
		},
		async performDrop(trigger: DropTriggerSource, snapshot: DropReadinessSnapshot): Promise<void> {
			if (this.dropAttempted) {
				return;
			}
			this.dropPipelineState = "dropping";
			this.logDropEvent("drop-start", { trigger, snapshot });
			try {
				await this.dropItems();
				this.dropAttempted = true;
				this.dropFlowCheckCancelled = true;
				this.dropPipelineState = "completed";
				this.logDropEvent("drop-complete", { trigger, snapshot });
			} catch (error) {
				this.dropPipelineState = "failed";
				this.logDropEvent("drop-error", { trigger, error });
				throw error;
			} finally {
				this.unlockInteractions();
			}
		},
		handleDropZoomProgress(zoom: number) {
			if (this.dropAttempted) {
				return;
			}
			if (!this.map) {
				return;
			}
			if (!this.isDropEligible()) {
				return;
			}
			if (zoom >= dropZoomThreshold && !this.dropLifecycleActive) {
				void this.runDropLifecycle("zoom-crossed", { forceAfterMs: 0, timeoutMs: 6000 });
			}
		},
		scheduleMapFallbackTimers() {
			this.clearMapLoadingTimers();
			if (!this.showMapLoadingOverlay) {
				return;
			}
			this.mapFallbackPromptTimer = window.setTimeout(() => {
				if (
					this.mapLoadingState === "ready" ||
					this.mapLoadingState === "fallback" ||
					this.mapLoadingState === "animating"
				) {
					return;
				}
				this.mapLoadingState = "fallback";
			}, 5000);
			this.mapAutoRevealTimer = window.setTimeout(() => {
				if (this.mapLoadingState === "ready" || this.mapLoadingState === "animating") {
					return;
				}
				this.fallbackRevealAllowed = true;
				this.revealMapSmoothly("fallback");
			}, 8000);
		},
		clearMapLoadingTimers() {
			if (this.mapFallbackPromptTimer !== null) {
				clearTimeout(this.mapFallbackPromptTimer);
				this.mapFallbackPromptTimer = null;
			}
			if (this.mapAutoRevealTimer !== null) {
				clearTimeout(this.mapAutoRevealTimer);
				this.mapAutoRevealTimer = null;
			}
		},
		clearOverlayHideTimer() {
			if (this.mapOverlayHideTimer !== null) {
				clearTimeout(this.mapOverlayHideTimer);
				this.mapOverlayHideTimer = null;
			}
		},
		beginOverlayHide(reason: "animation" | "fallback") {
			if (!this.showMapLoadingOverlay && !this.mapLoadingOverlayHidden) {
				return;
			}
			if (reason === "animation" && this.mapLoadingState !== "ready") {
				this.mapLoadingState = "animating";
			} else if (reason === "fallback") {
				this.mapLoadingState = "fallback";
			}
			if (!this.showMapLoadingOverlay) {
				return;
			}
			this.clearMapLoadingTimers();
			this.mapLoadingOverlayHidden = true;
			this.clearOverlayHideTimer();
			this.mapOverlayHideTimer = window.setTimeout(() => {
				this.showMapLoadingOverlay = false;
				this.mapOverlayHideTimer = null;
				this.mapLoadingOverlayHidden = false;
				this.mapLoadingState = "ready";
			}, 650);
		},
		onMapStyleLoaded() {
			if (!this.showMapLoadingOverlay && !this.mapLoadingOverlayHidden) {
				return;
			}
			if (this.mapLoadingState === "initializing") {
				this.mapLoadingState = "locating";
			} else if (this.mapLoadingState === "ready") {
				return;
			} else if (this.mapLoadingState !== "fallback" && this.mapLoadingState !== "animating") {
				this.mapLoadingState = "locating";
			}
			this.scheduleMapFallbackTimers();
		},
		async startIntroCameraAnimation(): Promise<void> {
			if (this.introAnimationRan) return;
			if (!this.map) return;
			if (this.userManualMapControl) return;
			const userCenter = this.userLocation as [number, number];
			if (!userCenter || (userCenter[0] === 0 && userCenter[1] === 0)) return;

			this.introAnimationRan = true;
			if (this.mapLoadingState !== "ready") {
				this.mapLoadingState = "animating";
			}
			const waitForMoveEnd = () => new Promise<void>((resolve) => {
				this.map!.once('moveend', () => resolve());
			});

			try {
				// Ensure style is loaded before animating
				if (!this.map!.isStyleLoaded()) {
					await new Promise<void>((resolve) => this.map!.once('load', () => resolve()));
				}
				// Cinematic "spaceship landing" approach
				// Start position: High altitude, looking straight down, offset from user
				const offsetDistance = 0.9; // ~100km offset in degrees (roughly)
				const startCenter: [number, number] = [
					userCenter[0] + offsetDistance * Math.cos(45 * Math.PI / 180), // Northeast offset
					userCenter[1] + offsetDistance * Math.sin(45 * Math.PI / 180)
				];

				// Set initial camera position (high altitude, straight down) - still hidden
				this.map!.jumpTo({
					center: startCenter,
					zoom: 3, // High altitude
					pitch: 85, // Almost straight down (90 degrees is not allowed in Mapbox)
					bearing: 225 // Looking towards user location
				});

				// Wait a moment for the jump to complete
				await new Promise(resolve => setTimeout(resolve, 200));

				// Pre-register movestart to avoid timing races, then start the cinematic descent
				const moveStartPromise: Promise<void> = new Promise<void>((resolve) => {
					this.map!.once('movestart', () => resolve());
				});

				// Start the cinematic descent
				console.log("Starting flyTo. Current zoom:", Number(this.map!.getZoom()).toFixed(2));
				this.map!.flyTo({
					center: userCenter, // End exactly on user
					zoom: 19.0, // Closer zoom for a tighter finale
					pitch: 62, // Final angled view
					bearing: 15, // Final bearing
					duration: 5000, // Faster, more cinematic
					essential: true,
					// Dramatic curve that starts fast and slows down (like landing)
					curve: 10.2, // Slightly lower curve for smoother approach
				});

				// Reveal only after movestart, then wait 500ms to avoid pre-animation state
				await moveStartPromise;
				console.log("movestart. Zoom:", Number(this.map!.getZoom()).toFixed(2));
				await new Promise((r) => setTimeout(r, 500));
				this.revealMapSmoothly();
				await waitForMoveEnd();
				console.log("moveend. Zoom:", Number(this.map!.getZoom()).toFixed(2));
			} catch (_) {
				// ignore animation errors
			}
		},
		async ensureZoomAtLeast(minZoom: number, options?: { timeoutMs?: number; forceAfterMs?: number }): Promise<boolean> {
			if (!this.map) {
				return false;
			}
			if (this.map.getZoom() >= minZoom) {
				return true;
			}
			const timeoutMs = options?.timeoutMs ?? 9000;
			const forceAfterMs = Math.max(0, Math.min(options?.forceAfterMs ?? 4500, timeoutMs));
			return new Promise<boolean>((resolve) => {
				let resolved = false;
				let timeoutId: number | null = null;
				let forceId: number | null = null;
				let cleanup: () => void;

				const finish = (value: boolean) => {
					if (!resolved) {
						resolved = true;
						cleanup();
						resolve(value);
					}
				};

				const handleZoom = () => {
					if (!this.map) {
						finish(false);
						return;
					}
					if (this.map.getZoom() >= minZoom) {
						finish(true);
					}
				};

				cleanup = () => {
					if (forceId !== null) {
						window.clearTimeout(forceId);
						forceId = null;
					}
					if (timeoutId !== null) {
						window.clearTimeout(timeoutId);
						timeoutId = null;
					}
					if (!this.map) {
						return;
					}
					this.map.off('zoom', handleZoom);
					this.map.off('move', handleZoom);
				};

				this.map!.on('zoom', handleZoom);
				this.map!.on('move', handleZoom);
				handleZoom();

				if (forceAfterMs > 0) {
					forceId = window.setTimeout(() => {
						if (!this.map || resolved) {
							return;
						}
						if (this.map.getZoom() < minZoom) {
							this.logDropEvent("force-zoom", {
								target: minZoom,
								zoom: Number(this.map.getZoom()).toFixed(2),
							});
							try {
								this.map.easeTo({ zoom: minZoom, duration: 900, essential: true });
							} catch (error) {
								console.warn('[DropPipeline] Failed to easeTo target zoom', error);
							}
						}
					}, forceAfterMs);
				}

				timeoutId = window.setTimeout(() => {
					if (!this.map) {
						finish(false);
						return;
					}
					finish(this.map.getZoom() >= minZoom);
				}, timeoutMs);
			});
		},
		renderZones() {
			if (!this.map) return;
			const pool = this.activePool;
			if (!pool || !pool.id) return;
			// Prefer zones from mapdata
			const zonesFromMapData = pool.dropZones || [];
			if (zonesFromMapData.length > 0) {
				const center: [number, number] = [this.userLocation[0], this.userLocation[1]];
				const nearest = selectNearestZones(zonesFromMapData, center, 6);
				mountZonesOnMap(this.map!, "drop-zones", nearest);
				return;
			}
			// Fallback to fetch by pool
			dropPoolStore().fetchDropPoolZones(pool.id!).then((zones) => {
				if (zones && zones.length > 0) {
					const center: [number, number] = [this.userLocation[0], this.userLocation[1]];
					const nearest = selectNearestZones(zones, center, 6);
					mountZonesOnMap(this.map!, "drop-zones", nearest);
				}
			});
		},
		geoJson() {
			let geoJSON = {
				type: "FeatureCollection",
				crs: {
					type: "name",
					properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
				},
				features: [],
			};
			geoJSON.features = this.locations.map((location) => {
				const id = uuidv4();
				location._id = id;
				return {
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: [location.longitude, location.latitude],
					},
					properties: {
						id: id,
						title: location.placeName,
						description: location.placeName,
					},
				} as never;
			});
			return geoJSON;
		},
		resetDropPoolLocationState() {
			dropPoolStore().saveLocationAccuracy(100000000);
			dropPoolStore().resetActiveDropPool();
			this.itemsDropped = false;
			this.dropAttempted = false;
			this.dropInFlight = false;
		},
		//Hide AR BTN
		hideARBtn() {
			this.arButton = document.querySelector("#ar");
			if (this.arButton) {
				this.arButton.style.pointerEvents = "none";
				this.arButton.style.opacity = "0";
			}
		},
		showARBtn() {
			if (this.arButton) {
				this.arButton.style.opacity = "1";
				this.arButton.style.pointerEvents = "auto";
			}
		},
		updateUserLocation() {
			this.userManualMapControl = false;
			this.moveUserIcon();
		},

		async getPoolsCategoriesPrizes() {
			await dropPoolStore().getMapData();
		},
		async getCampaignSegments() {
			await poolSegmentStore().getCampaignSegments();
		},
		getLocations() {
			this.locations = dropPoolStore().getDummyVatLocations;
		},
		getDroptype() {
			if (this.activePool) {
				this.dropType = this.activePool.dropType;
			}
		},
		initMap() {
			const mapElement = document.getElementById("map");
			if (mapElement) {
				mapElement.style.backgroundColor = "#000000"; // Keep black background during loading
				mapboxgl.accessToken = myAccessToken;
				let mapOptions: mapboxgl.MapboxOptions = {
					container: "map",
					style: this.mapStyle,
					center: this.userLocation,
					zoom: 2,
				};
				if (!this.map) {
					this.map = new mapboxgl.Map(mapOptions);
					this.map.on("dragstart", this.dragStartListener);
					this.map.on("load", this.loadListener);
					// Lock user interactions until drop completes or we decide otherwise
					this.lockInteractions();

					this.map.once('load', () => {
						console.log("Map style loaded - keeping hidden until animation");
						// Fail-safe reveal only after we allow fallback (e.g., out-of-zone) to avoid pre-animation exposure
						setTimeout(() => {
							if (this.fallbackRevealAllowed) {
								this.revealMapSmoothly("fallback");
							}
						}, 5000);
					});
				}
			}
		},
		loadListener() {
			this.onMapStyleLoaded();

		try {
			const fogPayload = mapVisualConfig.enableFog
				? (mapVisualConfig.fogOptions ?? {})
				: null;
			(this.map as any)?.setFog?.(fogPayload);
			} catch (e) {
				console.warn("Fog setup failed", e);
			}

			if (!mapVisualConfig.enable3dBuildings) {
				try {
					(this.map as any)?.setTerrain?.(null);
				} catch (e) {
					console.warn("Terrain disable failed", e);
				}
				for (const layerId of default3dLayerIds) {
					if (this.map!.getLayer(layerId)) {
						try {
							this.map!.removeLayer(layerId);
						} catch (error) {
							console.warn(`Failed to remove layer ${layerId}`, error);
						}
					}
				}
				try {
					const style = this.map!.getStyle();
					if (style && Array.isArray(style.layers)) {
						for (const layer of [...style.layers]) {
							if ((layer as any)?.type === "fill-extrusion" && layer.id && this.map!.getLayer(layer.id)) {
								try {
									this.map!.removeLayer(layer.id);
								} catch (error) {
									console.warn(`Failed to remove fill-extrusion layer ${layer.id}`, error);
								}
							}
						}
					}
				} catch (error) {
					console.warn("Failed to sweep fill-extrusion layers", error);
				}
				for (const sourceId of ["mapbox-dem"] as const) {
					if (this.map!.getSource(sourceId)) {
						try {
							this.map!.removeSource(sourceId);
						} catch (error) {
							console.warn(`Failed to remove source ${sourceId}`, error);
						}
					}
				}
			}

			this.map!.addSource("locations-source", {
				type: "geojson",
				data: this.geoJson() as any,
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
			});

			this.map!.addLayer({
				id: "clusters",
				type: "circle",
				source: "locations-source",
				filter: ["has", "point_count"],
				paint: {
					"circle-color": "rgba(0, 0, 0, 0)",
					"circle-radius": 30,
				},
			}, '3d-buildings');

			this.map!.addLayer({
				id: "cluster-count",
				type: "symbol",
				source: "locations-source",
				filter: ["has", "point_count"],
				layout: {},
			}, 'clusters');

			this.map!.on("zoom", this.zoomListener);
			this.map!.on("zoomend", this.zoomEndListener);

			this.map!.on("sourcedata", this.sourceDataListener);

			// Enable active pool checks as soon as the map is ready
			this.startActiveDropPoolCheck = true;

			// No cinematic zoom on load; using steady initial view
		},
		zoomListener() {
			const zoom = this.map!.getZoom();
			console.log("Map zoom:", Number(zoom).toFixed(2));
			this.handleDropZoomProgress(zoom);

			if (zoom >= 14 && !this.vatsAdded) {
				this.vatsAdded = true;
				this.addItemsToMap();
				this.loadRadiusCircleLayer(this.activeDropLocation);
			} else if (zoom < 14 && this.vatsAdded && this.dropType == "gps") {
				this.vatsAdded = false;
				this.removeItemsFromMap();
				this.removeRadiusCircleLayer();
			}
		},
		zoomEndListener() {
			const onClick = async (e: any) => {
				const features = this.map!.queryRenderedFeatures(e.point, {
					layers: ["clusters"],
				});
				const clusterId = features[0].properties!.cluster_id;
				const src = this.map!.getSource("locations-source") as any;

				src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
					if (err) return;
					const geom = features[0].geometry as any;
					this.map!.easeTo({
						center: geom.coordinates,
						zoom: zoom,
					});
				});
			};
			this.map!.off("click", "clusters", onClick);
			this.map!.on("click", "clusters", onClick);
			if (this.initZoom) {
				this.initZoom = false;
				this.startActiveDropPoolCheck = true;
				setTimeout(() => {
					if (
						this.activePool == null &&
						this.activeDropLocation == null &&
						this.locationAccuracy < accuracyThreshold
					) {
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "out-of-drop-zone",
						});
					}
				}, 3500);
			}
		},
		sourceDataListener() {
			this.updateLocations();
		},
		dragStartListener(e?: any) {
			if (this.interactionsLocked) {
				if (e && typeof e.preventDefault === 'function') e.preventDefault();
				if (e && e.originalEvent && typeof e.originalEvent.preventDefault === 'function') e.originalEvent.preventDefault();
				return;
			}
			this.userManualMapControl = true;
		},
		updateLocations() {
			const features = this.map!.querySourceFeatures("locations-source");
			const newMarkers = {} as { [key: string]: mapboxgl.Marker };

			for (let i = 0; i < features.length; i++) {
				const feature = features[i];
				if (
					feature.geometry.type == "Point" &&
					!feature.id &&
					feature.properties &&
					feature.properties!.id
				) {
					const loc = this.locations.find(
						(loc: DropLocation) => loc._id == feature.properties!.id
					);
					if (!loc) {
						return;
					}
					const coords = [loc!.longitude, loc!.latitude];
					const id = feature.properties!.id;
					let marker = this.locationMarkers[id];
					if (!marker) {
						marker = this.locationMarkers[id] = new mapboxgl.Marker({
							offset: [0, 0],
							color: loc!.pinColour ?? "#d51919",
						})
							.setLngLat(coords as [number, number])
							.setPopup(
								new mapboxgl.Popup({
									closeButton: true,
								}).setHTML(
									`<strong style="margin-bottom:5px;">${loc!.placeName}</strong>
									${loc!.image
										? `<img style="width:100%; height:100px; object-fit:contain; object-position:center;" src="${loc!.image
										}" />`
										: ""
									}
									${loc!.description ? `<p style="margin:0 5px;">${loc!.description}</p>` : ""}
									${loc!.url
										? `<a style="margin:0 5px;" href="${loc!.url
										}" target="_blank">Website</a>`
										: ""
									}
									`
								)
							);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}

				//add cluster
				if (feature.id && feature.geometry.type == "Point") {
					const id = feature.id + "_marker";
					const coords = feature.geometry.coordinates;
					let marker = this.locationMarkers[id];
					if (!marker) {
						marker = this.locationMarkers[id] = new mapboxgl.Marker(
							{}
						).setLngLat(coords as [number, number]);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}
				//add number
				if (feature.id && feature.geometry.type == "Point") {
					const id = feature.id + "_count";
					const coords = feature.geometry.coordinates;
					let marker = this.locationMarkers[id];
					if (!marker) {
						const el = document.createElement("div");
						el.className = "cluster-count";
						el.innerText = feature.properties!.point_count;
						marker = this.locationMarkers[id] = new mapboxgl.Marker(el, {
							offset: [0, -21],
						}).setLngLat(coords as [number, number]);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}
			}
			for (const id in this.locationMarkersOnScreen) {
				if (!newMarkers[id]) this.locationMarkersOnScreen[id].remove();
			}
			this.locationMarkersOnScreen = newMarkers;
			if (!this.loadedLocations) {
				this.loadedLocations = true;
				// Fallback: reveal the map once locations are ready, but only if allowed
				if (this.fallbackRevealAllowed) {
					this.revealMapSmoothly("fallback");
				}
				if (this.locations.length > 0 && this.locationBounds()) {
					this.map!.fitBounds(this.locationBounds(), {
						duration: 0,
						padding: {
							top: 100,
							bottom: 100,
							left: 100,
							right: 100,
						},
					});
				}
			}
		},
		// Backend sends swapped coordinates - fix by using item.latitude as lng, item.longitude as lat
		_getDisplayLngLat(item: { latitude: number; longitude: number }): [number, number] {
			const result = [item.latitude, item.longitude] as [number, number]; // Backend swapped: use lat as lng, lng as lat
			console.log(`🎯 _getDisplayLngLat for item:`, {
				backendLat: item.latitude,
				backendLng: item.longitude,
				mapboxFormat: result,
				userLocation: this.userLocation,
				note: "Backend sends swapped coords - using item.lat as lng, item.lng as lat"
			});
			return result; // Mapbox expects [lng, lat]
		},
		revealMapSmoothly(reason: "animation" | "fallback" = "animation") {
			const el = document.getElementById('map');
			if (el) {
				el.style.transition = 'opacity 0.8s ease-out';
				el.style.opacity = '1';
				el.style.backgroundColor = 'transparent';
			}
			this.beginOverlayHide(reason);
		},
		lockInteractions() {
			if (!this.map) return;
			this.interactionsLocked = true;
			try { this.map.boxZoom.disable(); } catch (_) { }
			try { this.map.dragPan.disable(); } catch (_) { }
			try { this.map.scrollZoom.disable(); } catch (_) { }
			try { this.map.dragRotate.disable(); } catch (_) { }
			try { this.map.keyboard.disable(); } catch (_) { }
			try { this.map.doubleClickZoom.disable(); } catch (_) { }
			try { (this.map as any).touchZoomRotate?.disable(); } catch (_) { }
		},
		unlockInteractions() {
			if (!this.map) return;
			this.interactionsLocked = false;
			try { this.map.boxZoom.enable(); } catch (_) { }
			try { this.map.dragPan.enable(); } catch (_) { }
			try { this.map.scrollZoom.enable(); } catch (_) { }
			try { this.map.dragRotate.enable(); } catch (_) { }
			try { this.map.keyboard.enable(); } catch (_) { }
			try { this.map.doubleClickZoom.enable(); } catch (_) { }
			try { (this.map as any).touchZoomRotate?.enable(); } catch (_) { }
		},
		addUserMarker(): void {
			const user = document.createElement("div");
			user.style.backgroundColor = `rgba(51, 153, 255, 0.3)`;
			user.style.width = "50px";
			user.style.height = "50px";
			user.style.borderRadius = "50px";
			user.style.animation = "anim 1s infinite alternate";
			user.style.display = "flex";
			user.style.justifyContent = "center";
			user.style.alignItems = "center";
			const elem = document.createElement("div");
			elem.style.backgroundColor = `#3399ff`;
			elem.style.width = "15px";
			elem.style.height = "15px";
			elem.style.borderRadius = "10px";
			elem.style.boxShadow = "2px 2px 3px rgba(0, 0, 0, 0.1) inset";

			user.appendChild(elem);
			if (this.userMarker) {
				this.userMarker.remove();
			}

			this.userMarker = new mapboxgl.Marker({
				draggable: false,
				element: user,
			}).setLngLat(this.userLocation);
			if (this.map) {
				this.userMarker.addTo(this.map);
			}
		},

		async dropItems(force = false) {
			if (!force && (this.dropInFlight || this.itemsDropped || this.dropAttempted)) {
				this.logDropEvent("skip-drop-repeat", {
					dropInFlight: this.dropInFlight,
					itemsDropped: this.itemsDropped,
					dropAttempted: this.dropAttempted,
				});
				return;
			}

			this.dropInFlight = true;
			let updated = false;
			try {
				// Check if there are any pending items
				const pendingItems = vItemStore().getPendingItems;

			if (pendingItems.length > 0) {
				console.log("Found pending items:", pendingItems);

				// Make sure we have valid user location before updating
				if (this.userLocation[0] === 0 && this.userLocation[1] === 0) {
					console.warn("Cannot update item location: User location not available yet");
					return; // Exit early if we don't have a valid location
				}

				console.log("Updating pending items with location:", this.userLocation);

					// Only update items that don't have coordinates yet
					for (const item of pendingItems) {
						console.log(`🔍 CHECKING PENDING ITEM ${item.id}:`, {
							itemLat: item.latitude,
							itemLng: item.longitude,
							hasValidCoords: !(!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)),
							userLocation: this.userLocation
						});
						// Only update if item genuinely lacks coordinates
						if (!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)) {
							console.log(`⚠️ OVERRIDING item ${item.id} with user location:`, this.userLocation);
							vItemStore().updateItemLocation({
								id: item.id!,
								latitude: this.userLocation[1], // Use correct lat from user location
								longitude: this.userLocation[0] // Use correct lng from user location
							});
						} else {
							console.log(`✅ PRESERVING item ${item.id} coordinates:`, { lat: item.latitude, lng: item.longitude });
						}
					}
					updated = true;
				} else if (this.activePool) {
					// No pending items, drop new items
					const walletID = authStore().walletID;
					console.log("dropping!!!!!");
					await vItemStore().dropItems({
						walletID: walletID || "",
						segmentID: "null",
						poolID: this.activePool!.id!,
						userLocation: [this.userLocation[1], this.userLocation[0]],
					});
					updated = true;
				}

				if (updated) {
					this.itemsDropped = true;
				}
			} finally {
				this.dropInFlight = false;
			}

			this.addItemsToMap();
		},
		async addRemoveDroppedItems(poolID: string) {
			await vItemStore().recycleItems({
				walletID: this.walletID!,
				poolID: poolID,
			});
			// Only drop if zoom is at target level (avoid dropping during animation)
			if (!this.map || this.map.getZoom() >= dropZoomThreshold) {
				await this.dropItems();
			}
			this.addItemsToMap();
		},
		moveUserIcon() {
			if (this.map && this.userMarker) {
				this.userMarker.remove();
				this.addUserMarker();
				// No automatic flyTo on user location updates for now
			}
		},
		addItemsToMap(): void {
			// Clear existing markers and symbol layers
			this.clearItemsFromMap();

			// Get all items, including both dropped and pending items
			const allItems = [...this.vItems];

			console.log("🗺️ ADDING ITEMS TO MAP:", allItems.map(i => ({
				id: i.id,
				stat: i.itemStatus,
				lat: i.latitude,
				lng: i.longitude
			})));

			// Filter out items without valid coordinates
			const validItems = allItems.filter(item => {
				console.log(`🔍 VALIDATING ITEM ${item.id}:`, {
					status: item.itemStatus,
					lat: item.latitude,
					lng: item.longitude,
					missingCoords: !item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)
				});
				if (item.itemStatus == "pending") {
					// Only fallback to user location if coordinates are truly missing
					if (!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)) {
						console.warn(`FALLBACK: Item ${item.id} missing coordinates, using user location:`, item);
						item.latitude = this.userLocation[1]; // Correct lat
						item.longitude = this.userLocation[0]; // Correct lng
						return true;
					}
				}
				return true;
			});

			console.log(`Found ${validItems.length} items with valid coordinates`);

			if (validItems.length === 0) {
				return;
			}

			// Load marker images and create GeoJSON source data for symbol layer
			const imagePromises: Promise<void>[] = [];
			const vitemsGeoJSON = {
				type: "FeatureCollection" as const,
				features: validItems.map((item, index) => {
					const coords = this._getDisplayLngLat(item);
					const category = categoryStore().getItemByID(item.categoryID);
					const imageId = `vitem-${index}`;

					console.log(`📍 CREATING GEOJSON FEATURE for item ${item.id}:`, {
						originalCoords: { lat: item.latitude, lng: item.longitude },
						geoJsonCoords: coords,
						status: item.itemStatus,
						markerUrl: item.marker,
						imageId: imageId
					});

					// Try to load the marker image into Mapbox (may fail due to CORS)
					console.log(`ITEM MARKER: ${item.marker}`);
					console.log(`IMAGE ID: ${imageId}`);
					console.log(`MAP HAS IMAGE: ${this.map!.hasImage(imageId)}`);
					if (item.marker && !this.map!.hasImage(imageId)) {
						console.log(`LOADING IMAGE FOR ITEM ${JSON.stringify(item)}`);
						const imagePromise = new Promise<void>((resolve) => {
							this.map!.loadImage(item.marker, (error: any, image: any) => {
								if (error) {
									console.warn(`⚠️ CORS blocked image for ${item.id}, will use HTML marker fallback:`, error);
									// Mark this item for HTML marker fallback
									(item as any)._useFallback = true;
									resolve();
									return;
								}
								if (image && !this.map!.hasImage(imageId)) {
									try {
										this.map!.addImage(imageId, image);
										console.log(`🖼️ Added image ${imageId} to map`);
									} catch (webglError) {
										console.warn(`⚠️ WebGL error adding image for ${item.id}, will use HTML marker fallback:`, webglError);
										(item as any)._useFallback = true;
									}
								}
								resolve();
							});
						});
						imagePromises.push(imagePromise);
					}

					return {
						type: "Feature" as const,
						geometry: {
							type: "Point" as const,
							coordinates: coords
						},
						properties: {
							id: item.id,
							imageId: imageId,
							categoryID: item.categoryID,
							imageSize: category?.imageSize || 32,
							status: item.itemStatus
						}
					};
				})
			};

			// Wait for all images to load before adding the layer
			Promise.all(imagePromises).then(() => {
				// Split items into symbol layer (successful) and HTML marker fallback
				const symbolItems = validItems.filter(item => !(item as any)._useFallback);
				const fallbackItems = validItems.filter(item => (item as any)._useFallback);

				console.log(`🔀 Using symbol layer for ${symbolItems.length} items, HTML fallback for ${fallbackItems.length} items`);

				// Add symbol layer for items that loaded successfully
				if (symbolItems.length > 0) {
					const symbolGeoJSON = {
						...vitemsGeoJSON,
						features: vitemsGeoJSON.features.filter((feature: any) => {
							const item = validItems.find(i => i.id === feature.properties.id);
							return !(item as any)?._useFallback;
						})
					};
					this.addVitemsSymbolLayer(symbolGeoJSON);
				}

				// Add HTML markers for CORS-blocked items
				if (fallbackItems.length > 0) {
					this.addFallbackHtmlMarkers(fallbackItems);
				}
			});

			if (validItems.length > 0) {
				this.showARBtn();
			}
		},
		addVitemsSymbolLayer(vitemsGeoJSON: any): void {
			console.log(`ADDING VITEMS SYMBOL LAYER: ${JSON.stringify(vitemsGeoJSON)}`);
			// Add source if it doesn't exist
			if (!this.map!.getSource('vitems-source')) {
				this.map!.addSource('vitems-source', {
					type: 'geojson',
					data: vitemsGeoJSON
				});
				console.log("🔧 Added vitems-source to map");
			} else {
				// Update existing source
				(this.map!.getSource('vitems-source') as mapboxgl.GeoJSONSource).setData(vitemsGeoJSON);
				console.log("🔄 Updated vitems-source data");
			}

			// Add symbol layer if it doesn't exist
			if (!this.map!.getLayer('vitems-layer')) {
				this.map!.addLayer({
					id: 'vitems-layer',
					type: 'symbol',
					source: 'vitems-source',
					layout: {
						'icon-image': ['get', 'imageId'], // Use the preloaded image ID
						'icon-size': 0.06, // Fixed reasonable size for map markers
						'icon-anchor': 'bottom', // Anchor to bottom for ground positioning
						'icon-allow-overlap': true,
						'icon-ignore-placement': true,
						'icon-pitch-alignment': 'auto', // Align with map pitch
						'icon-rotation-alignment': 'auto' // Align with map rotation
					},
					paint: {
						'icon-opacity': 1
					}
				} as any);

				console.log("🎨 Added vitems symbol layer to map");

				// Add click handler for symbol layer
				this.map!.on('click', 'vitems-layer', (e) => {
					if (e.features && e.features.length > 0) {
						const feature = e.features[0];
						const itemId = feature.properties?.id;
						if (itemId) {
							console.log(`🖱️ Clicked on vitem: ${itemId}`);
							this.selectedMarkerIndex = itemId;
							navigationStore().emitAction({
								type: ActionType.OPEN_POPUP,
								value: "claim-item",
							});
						}
					}
				});

				// Change cursor on hover
				this.map!.on('mouseenter', 'vitems-layer', () => {
					this.map!.getCanvas().style.cursor = 'pointer';
				});

				this.map!.on('mouseleave', 'vitems-layer', () => {
					this.map!.getCanvas().style.cursor = '';
				});
			}

			console.log("✅ Successfully added vitems as symbol layer");
		},
		addFallbackHtmlMarkers(items: any[]): void {
			console.log(`🔄 Adding ${items.length} HTML marker fallbacks`);

			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				const category = categoryStore().getItemByID(item.categoryID);

				if (!category) {
					console.error("Category not found for fallback item:", item);
					continue;
				}

				console.log(`📍 CREATING FALLBACK HTML MARKER for item ${item.id}:`, {
					originalCoords: { lat: item.latitude, lng: item.longitude },
					displayCoords: this._getDisplayLngLat(item),
					status: item.itemStatus
				});

				const elem = document.createElement("div");
				elem.setAttribute("id", item.id!);
				elem.style.backgroundImage = `url('${item.marker}')`;
				elem.style.backgroundSize = "contain";
				elem.style.backgroundPosition = "center";
				elem.style.backgroundRepeat = "no-repeat";
				elem.style.width = `${category.imageSize}px`;
				elem.style.height = `${category.imageSize}px`;
				elem.style.zIndex = "1000";
				elem.style.pointerEvents = "auto";
				elem.classList.add("vat-marker");

				const markerCoords = this._getDisplayLngLat(item);
				const marker = new mapboxgl.Marker({
					draggable: false,
					element: elem,
					anchor: 'bottom', // Anchor to bottom so marker sits on ground
					pitchAlignment: 'auto', // Align with map pitch  
					rotationAlignment: 'auto' // Align with map rotation
				}).setLngLat(markerCoords);

				// Add to map and store reference
				marker.addTo(this.map!);
				this.markers.push(marker);

				// Add click handler
				elem.addEventListener("click", () => {
					this.selectedMarkerIndex = item.id!;
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "claim-item",
					});
				});

				console.log(`🎯 Added fallback marker for ${item.id}`);
			}
		},
		removeItemsFromMap(): void {
			this.clearItemsFromMap();
		},
		clearItemsFromMap(): void {
			// Remove HTML markers (legacy)
			if (this.markers.length > 0) {
				for (let i = this.markers.length - 1; i >= 0; i--) {
					this.markers[i].remove();
					this.markers.splice(i, 1);
				}
				console.log("🧹 Cleared HTML markers");
			}

			// Remove symbol layer and source
			if (this.map) {
				if (this.map.getLayer('vitems-layer')) {
					this.map.removeLayer('vitems-layer');
					console.log("🧹 Removed vitems symbol layer");
				}
				if (this.map.getSource('vitems-source')) {
					this.map.removeSource('vitems-source');
					console.log("🧹 Removed vitems source");
				}
			}
		},
		removeClaimedItem(itemId: string): void {
			// Remove from HTML markers first (fallback markers)
			for (let i = this.markers.length - 1; i >= 0; i--) {
				const markerElement = this.markers[i].getElement();
				if (markerElement && markerElement.id === itemId) {
					this.markers[i].remove();
					this.markers.splice(i, 1);
					console.log(`🎯 Removed HTML marker for claimed item: ${itemId}`);
					break;
				}
			}

			// Update symbol layer source data by filtering out the claimed item
			const source = this.map?.getSource('vitems-source') as mapboxgl.GeoJSONSource;
			if (source) {
				// Get current items (excluding the claimed one)
				const remainingItems = this.vItems.filter(item => item.id !== itemId);

				// Recreate GeoJSON with remaining items
				const updatedGeoJSON = {
					type: "FeatureCollection" as const,
					features: remainingItems.map((item, index) => {
						const coords = this._getDisplayLngLat(item);
						const category = categoryStore().getItemByID(item.categoryID);
						const imageId = `vitem-${index}`;

						return {
							type: "Feature" as const,
							geometry: {
								type: "Point" as const,
								coordinates: coords
							},
							properties: {
								id: item.id,
								imageId: imageId,
								categoryID: item.categoryID,
								imageSize: category?.imageSize || 32,
								status: item.itemStatus
							}
						};
					})
				};

				// Update the source with filtered data
				source.setData(updatedGeoJSON);
				console.log(`🎯 Updated symbol layer, removed claimed item: ${itemId}`);
			}
		},
		loadRadiusCircleLayer(location: DropLocation | null) {
			if (!this.map!.getLayer("radius-circles") && location) {
				const circles: {
					latitude: number;
					longitude: number;
					radius: number;
				}[] = [];

				circles.push({
					latitude: location.latitude,
					longitude: location.longitude,
					radius: location.radius! / 1000,
				});

				this.map!.addLayer({
					id: "radius-circles",
					type: "fill",
					source: addDropZoneCircles(circles) as mapboxgl.AnySourceData,
					layout: {},
					paint: {
						"fill-color": "#2EB3FF",
						"fill-opacity": 0.2,
					},
				});
			}
		},
		removeRadiusCircleLayer() {
			if (this.map!.getLayer("radius-circles")) {
				this.map!.removeLayer("radius-circles");
				this.map!.removeSource("radius-circles");
			}
		},
		//Helper functions
		locationBounds(): mapboxgl.LngLatBounds {
			// const bounds: [number, number][] = [];
			const bounds = new mapboxgl.LngLatBounds();

			for (let i = 0; i < this.locations.length; i++) {
				bounds.extend([
					this.locations[i].longitude,
					this.locations[i].latitude,
				]);
			}
			console.log(bounds);
			return bounds;
		},


		async claimItem() {
			// selectedMarkerIndex is now the item ID (set in symbol layer click handler)
			const id = this.selectedMarkerIndex;
			dropPoolStore().setLoading(true);

			try {
				const pendingItem = vItemStore().getPendingItems.find(
					(item) => item.id == id
				);
				await vItemStore().claimItem(id);

				// Remove only the claimed item without affecting others
				this.removeClaimedItem(id);

				// Clear selection
				this.selectedMarkerIndex = "";

				dropPoolStore().setLoading(false);
				// check selected vitem skipToGameplay
				const vitem = vItemStore().items.find(
					(item) => item.id == id
				);

				if (pendingItem && pendingItem.isDeepLink) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				// if skipToGameplay is true, skip to the vault page
				// this will be used in the vault to skip to the prize page rendering
				if (vitem && vitem.skipToGameplay) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				if (this.vItems.length == 0) {
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "vault",
					});
				}
			} catch (error) {
				console.error(error);
				this.selectedMarkerIndex = "";
				dropPoolStore().setLoading(false);
			}
		},
	},
	computed: {
		//Store getters
		activePool(): DropPool | null {
			return dropPoolStore().getActivePool;
		},
		loading(): boolean {
			return dropPoolStore().getLoading;
		},
		walletID(): string | null {
			return authStore().getWalletID;
		},
		activeDropLocation(): DropLocation | null {
			return dropPoolStore().getActiveDropLocation;
		},
		vItems(): VItem[] {
			// Get all items
			const droppedItems = vItemStore().getDroppedItems || [];
			const pendingItems = vItemStore().getPendingItems || [];

			// Combine both types of items
			const allItems = [...droppedItems, ...pendingItems];

			// Log the items for debugging
			console.log("vItems computed property:", {
				droppedItems: droppedItems.length,
				pendingItems: pendingItems.length,
				allItems: allItems.length
			});

			return allItems;
		},
		action(): Action {
			return navigationStore().getAction;
		},
		userLocation(): [number, number] {
			return dropPoolStore().getUserLocation;
		},
		hasResolvedUserLocation(): boolean {
			const [lng, lat] = this.userLocation;
			return !(lng === 0 && lat === 0);
		},
		locationAccuracy(): number {
			return dropPoolStore().getLocationAccuracy;
		},
		watcher(): number {
			return dropPoolStore().getWatcher;
		},
		poorAccuracy(): boolean {
			return navigationStore().getPoorAccuracy;
		},
		mapLoadingMessage(): string {
			switch (this.mapLoadingState) {
				case "initializing":
					return "Preparing the map...";
				case "locating":
					return "Finding your position...";
				case "animating":
					return "Flying to your location...";
				case "fallback":
					return "Showing the map without a precise lock.";
				case "failed":
					return "Unable to detect your location.";
				case "ready":
				default:
					return "";
			}
		},
		shouldDisplayAccuracy(): boolean {
			if (!this.hasResolvedUserLocation) {
				return false;
			}
			if (this.mapLoadingState === "ready" || this.mapLoadingState === "failed") {
				return false;
			}
			const accuracy = this.locationAccuracy;
			return Number.isFinite(accuracy) && accuracy > 0 && accuracy <= 5000;
		},
		accuracyLabel(): string {
			if (!this.shouldDisplayAccuracy) {
				return "";
			}
			const rounded = Math.max(5, Math.round(this.locationAccuracy));
			return `Accuracy ±${rounded} m`;
		},
	},
});
</script>

<style lang="scss">
#map {
	position: relative;
	width: 100%;
	height: 100%;
	background-color: #000;
	transition: opacity 0.4s ease;
}

.map-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: #000;
	color: #ffffff;
	z-index: 9000;
	opacity: 1;
	transition: opacity 0.45s ease-out;
}

.map-loading-overlay--hidden {
	opacity: 0;
	pointer-events: none;
}

.map-loading-overlay__panel {
	max-width: 280px;
	text-align: center;
	line-height: 1.4;
}

.map-loading-overlay__spinner {
	width: 48px;
	height: 48px;
	margin: 0 auto 18px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.24);
	border-top-color: #ffffff;
	animation: map-loading-spin 0.9s linear infinite;
}

.map-loading-overlay__message {
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
}

.map-loading-overlay__accuracy {
	margin-top: 10px;
	font-size: 0.85rem;
	opacity: 0.8;
}

@keyframes map-loading-spin {
	to {
		transform: rotate(360deg);
	}
}

.map-interaction-blocker {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10000;
	background: transparent;
	pointer-events: all;
	/* Block all gestures and mouse interactions on all devices */
	touch-action: none;
	/* Block all mouse events on desktop */
	cursor: not-allowed;
	user-select: none;
}

.drop-button {
	position: absolute;
	right: 15px;
	top: 15px;
}

.dev-info {
	position: fixed;
	top: 0;
	left: 0;
	color: #fff;
}

@keyframes anim {
	from {
		background-color: rgba(51, 153, 255, 0.3);
	}

	to {
		background-color: rgba(51, 153, 255, 0.1);
	}
}

.cluster-count {
	color: #000;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 100px;
	font-size: 11px;
	width: 20px;
	height: 20px;
	background-color: #fff;
}

.accuracy-indicator {
	position: absolute;
	z-index: 1;
	top: 10px;
	left: 50%;
	transform: translate(-50%, 0);
	background-color: #ff00007b;
	color: #fff;
	padding: 5px 10px;
	border-radius: 100px;
	box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.location-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9999;
	pointer-events: none;
}

.location-loading-text {
	color: #000000;
	font-size: 18px;
	font-weight: 500;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
